// src/pages/FundPlanner/components/FundStructurer/CashflowSufficiency.jsx
import React, { useMemo } from "react";
import SectionCard from "components/SectionCard";
import ExcelGrid from "components/ExcelGrid";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import ChartTooltip from "components/ChartTooltip";
import { makeNiceScaleZeroBased } from "lib/chartScale";
import {
  buildStructurerChartData,
  buildStructurerSufficiencyRows,
} from "pages/FundPlanner/reports/structurerReportData";

/**
 * Fund Cash Flows
 * ---------------------------------------------------------
 * Net Fund chart: withdrawal bars plus fund-balance line
 * Gross Fund chart: available-balance bars on withdrawal dates
 */
export default function CashflowSufficiency({
  draws = [],
  allocations = [],
  inflows: inflowsProp = [],
  fundMode = "gross",
}) {
  const cleanMoney = (v) => {
    const n = Number(v || 0);
    return Math.abs(n) < 0.005 ? 0 : n;
  };

  const fmt2 = (v) =>
    v != null
      ? cleanMoney(v).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "";

  const rows = useMemo(
    () =>
      buildStructurerSufficiencyRows({
        draws,
        investments: allocations,
        inflows: inflowsProp,
        fundMode,
      }),
    [allocations, inflowsProp, draws, fundMode]
  );
  const columns = [
    {
      key: "date",
      label: "Date",
      date: true,
      className: "numeric",
    },
    { key: "type", label: "Type" },
    { key: "description", label: "Description" },
    {
      key: "netChange",
      label: "Net Change ($)",
      numeric: true,
      className: "text-right numeric",
      headerClassName: "text-right",
      format: fmt2,
    },
    {
      key: "balance",
      label: "Balance ($)",
      numeric: true,
      className: "text-right numeric",
      headerClassName: "text-right",
      format: fmt2,
    },
  ];

  const chartData = useMemo(
    () =>
      buildStructurerChartData({ draws, sufficiencyRows: rows, fundMode })
        .sufficiency,
    [draws, rows, fundMode]
  );
  const yTickFormatter = (v) => `$${(cleanMoney(v) / 1e6).toFixed(1)}M`;

  const tooltipFormatter = (val, name) => [
    `$${cleanMoney(val).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`,
    name,
  ];

  // --- Nice Y scale (zero-based) ---
  const scale = useMemo(() => {
    const maxVal = Math.max(
      ...chartData.map((d) => Number(d.balance) || 0),
      ...chartData.map((d) => Number(d.drawAmount) || 0),
      ...chartData.map((d) => Number(d.availableBalance) || 0),
      0
    );

    return makeNiceScaleZeroBased(maxVal, 6, 1);
  }, [chartData]);

  if (!rows?.length) return null;

  return (
    <SectionCard title="Fund Cash Flows">
      {/* ---------- Chart ---------- */}
      <div className="h-96 bg-card border border-border rounded-xl p-4 mb-4 shadow-sm">
        <ResponsiveContainer>
          <ComposedChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />

            <XAxis dataKey="date" />

            <YAxis
              domain={[scale.niceMin, scale.niceMax]}
              ticks={scale.ticks}
              tickFormatter={yTickFormatter}
            />

            <Tooltip
              content={<ChartTooltip formatter={tooltipFormatter} />}
            />

            <Legend
              verticalAlign="bottom"
              align="center"
              height={18}
              wrapperStyle={{
                bottom: 0,
                fontSize: 13,
                lineHeight: "1em",
                marginTop: 0,
                padding: 0,
              }}
            />

            {fundMode === "gross" ? (
              <Bar
                dataKey="availableBalance"
                fill="var(--chart-1)"
                name="Available Balance"
                barSize={18}
              />
            ) : (
              <>
                <Bar
                  dataKey="drawAmount"
                  fill="var(--chart-1)"
                  name="Fund Withdrawals"
                  barSize={18}
                />
                <Line
                  type="monotone"
                  dataKey="balance"
                  name="Fund Balance"
                  stroke="var(--chart-3)"
                  strokeWidth={3}
                  dot={{ r: 3, fill: "var(--chart-3)" }}
                  activeDot={{ r: 5 }}
                  connectNulls
                />
              </>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* ---------- Original Table (unchanged) ---------- */}
      <ExcelGrid columns={columns} rows={rows} />
    </SectionCard>
  );
}